import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FilterOptions } from 'Components/widgets/filter-options/FilterOptions';
import { FilterToggler } from 'Components/widgets/filter-toggler/FilterToggler';
import { classnames } from 'Utils/classnames/classnames.utils';

type Props = FilterItem & {
  className?: string;
  hasForceClose?: boolean;
  name: string;
  onChange: FilterItemOnChangeFunc;
  onClear?: (name: string) => void;
  onClick?: (name: string) => void;
  onClickOption?: (option: FilterOptionProps) => void;
  portalName: string;
  setOptions: (options: Array<FilterOptionProps>) => void;
};

const getSelectedOptions = (options = []) =>
  options.reduce((acc, o) => (o.isSelected ? [...acc, o.value] : acc), []);

export const Filter = ({
  className,
  customRender,
  customRenderParent,
  hasForceClose = false,
  label,
  name,
  onChange,
  onClear,
  onClick,
  onClickOption,
  options = [],
  portalName: _portalName,
  setOptions,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownTop, setDropdownTop] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const selectedOptions = getSelectedOptions(options);

  useEffect(() => {
    if (isOpen && hasForceClose) {
      setIsOpen(false);
    }
  }, [hasForceClose]);

  // Compute dropdown top position (below the filter bar, not just the button)
  useLayoutEffect(() => {
    if (isOpen && wrapperRef.current) {
      // Walk up to find the fixed filter bar container
      let el: HTMLElement | null = wrapperRef.current;
      while (el && !el.classList.contains('fixed')) {
        el = el.parentElement;
      }
      const bottom = el
        ? el.getBoundingClientRect().bottom
        : wrapperRef.current.getBoundingClientRect().bottom;
      setDropdownTop(bottom);
    }
  }, [isOpen]);

  const toggleOption = (option: FilterOptionProps): void => {
    const newOptions = [...options];
    const optionToggeledIndex = newOptions.findIndex((opt) => opt.id === option.id);
    const updatedOption = {
      ...newOptions[optionToggeledIndex],
      isSelected: !newOptions[optionToggeledIndex].isSelected,
    };
    newOptions[optionToggeledIndex] = updatedOption;
    setOptions(newOptions);
    onChange(name, getSelectedOptions(newOptions), option.value);
    onClickOption?.(updatedOption);
  };

  const clearAllSelected = () => {
    const newOptions = options.map((opt) => ({ ...opt, isSelected: false }));
    setOptions([...newOptions]);
    onClear && onClear(name);
  };

  return (
    <div className={classnames('relative', className)} ref={wrapperRef}>
      {!customRenderParent ? (
        <FilterToggler
          isActive={isOpen}
          isDisabled={options.length === 0}
          onClear={clearAllSelected}
          onClick={() => {
            setIsOpen(!isOpen);
            onClick?.(name);
          }}
          selectedOptionsNumber={selectedOptions.length}
        >
          {label}
        </FilterToggler>
      ) : (
        <div className="flex items-center whitespace-nowrap">
          {customRenderParent(selectedOptions, onChange, options.length === 0)}
        </div>
      )}
      {isOpen && (
        <React.Fragment>
          {/* Backdrop: closes on any outside click */}
          <div className="fixed inset-0 z-[9]" onClick={() => setIsOpen(false)} />
          {/* Dropdown: full-width, positioned below the filter bar */}
          <div
            className="fixed inset-x-0 z-[10] border-b-1 border-lightGrey bg-white shadow-diffuse"
            style={{ top: dropdownTop }}
          >
            {customRender ? (
              <div className="mx-auto flex max-w-site p-15">
                {customRender(options, selectedOptions, toggleOption)}
              </div>
            ) : (
              <div className="mx-auto max-w-site p-15">
                <FilterOptions
                  name={name}
                  onChange={(value) => {
                    const clickedOption = options.find((opt) => opt.value === value);
                    clickedOption && toggleOption(clickedOption);
                  }}
                  options={options.map((option) => ({
                    ...option,
                    isSelected: option.isSelected,
                    label: (
                      <span className="capitalize">
                        {typeof option.label === 'string'
                          ? option.label.toLowerCase()
                          : option.label}
                      </span>
                    ),
                  }))}
                />
              </div>
            )}
          </div>
        </React.Fragment>
      )}
    </div>
  );
};
